import type { IconName, IconTone } from '../ui/Icon';

/**
 * Navigation model for the control panel.
 *
 * Grouped the way an operator works (overview, community, moderation, system)
 * rather than by API controller. Every entry carries its own icon and hue: the
 * icon is what a collapsed rail shows instead of a label, and keeping the hue on
 * the model means the rail, the page header and that module's row actions cannot
 * drift to different colours.
 *
 * `sample` marks a screen whose data has no API endpoint yet: the shell shows a
 * badge next to it and the page itself carries a notice, so a sample screen can
 * never be mistaken for live data.
 */
export interface AdminNavItem {
  to: string;
  labelKey: string;
  icon: IconName;
  tone: IconTone;
  /** `end` keeps a parent link from matching every child route. */
  end?: boolean;
  sample?: boolean;
  /** Sub-modules, rendered as a disclosure under the parent. */
  children?: readonly AdminNavItem[];
  /**
   * A child that re-links its parent's own page, so an expanded disclosure
   * lists every destination in the module instead of silently omitting the
   * default one.
   *
   * Excluded from route lookup: it shares its parent's `to`, and a tie there
   * would otherwise make the breadcrumb name the child ("Affiliates /
   * Affiliates") depending on sort order. The parent is the entry a path
   * resolves to.
   */
  defaultChild?: boolean;
}

export interface AdminNavSection {
  titleKey: string;
  items: readonly AdminNavItem[];
}

export const NAV_SECTIONS: readonly AdminNavSection[] = [
  {
    titleKey: 'nav.sections.overview',
    items: [
      { to: '/', labelKey: 'nav.dashboard', icon: 'dashboard', tone: 'blue', end: true },
      { to: '/statistics', labelKey: 'nav.statistics', icon: 'chart', tone: 'indigo' },
    ],
  },
  {
    titleKey: 'nav.sections.community',
    items: [
      { to: '/users', labelKey: 'nav.users', icon: 'users', tone: 'blue' },
      {
        to: '/affiliates',
        labelKey: 'nav.affiliates',
        icon: 'star',
        tone: 'amber',
        // No `end`: the parent marks "you are somewhere in this module", which
        // is how the Reported content group already behaves. It stays clickable
        // because, unlike that group, this one has a page of its own.
        children: [
          {
            to: '/affiliates',
            labelKey: 'nav.affiliatesAll',
            icon: 'list',
            tone: 'amber',
            // Exact match, or the list would stay highlighted while the
            // qualification rule page is the one actually open.
            end: true,
            defaultChild: true,
          },
          {
            // Settings for the whole programme rather than for one affiliate,
            // so it belongs beside the list and not inside a row's dialogs.
            to: '/affiliates/qualification-rule',
            labelKey: 'nav.affiliateQualificationRule',
            icon: 'settings',
            tone: 'amber',
          },
        ],
      },
      { to: '/agents', labelKey: 'nav.agents', icon: 'robot', tone: 'violet' },
      { to: '/posts', labelKey: 'nav.posts', icon: 'post', tone: 'teal' },
      { to: '/groups', labelKey: 'nav.groups', icon: 'group', tone: 'cyan' },
    ],
  },
  {
    titleKey: 'nav.sections.moderation',
    items: [
      {
        to: '/moderation',
        labelKey: 'nav.moderation',
        icon: 'flag',
        tone: 'rose',
        children: [
          {
            to: '/moderation/pending',
            labelKey: 'nav.moderationPending',
            icon: 'clock',
            tone: 'rose',
          },
          {
            to: '/moderation/reviewed',
            labelKey: 'nav.moderationReviewed',
            icon: 'check',
            tone: 'emerald',
          },
        ],
      },
      {
        // Automated screening, kept apart from the Reported content group
        // above: that queue is what members flagged, this one is what the
        // classifier held before anybody saw it.
        to: '/content-moderation',
        labelKey: 'nav.contentModeration',
        icon: 'shield',
        tone: 'violet',
        children: [
          {
            to: '/content-moderation/queue',
            labelKey: 'nav.contentModerationQueue',
            icon: 'clock',
            tone: 'violet',
          },
          {
            to: '/content-moderation/controls',
            labelKey: 'nav.contentModerationControls',
            icon: 'power',
            tone: 'slate',
          },
          {
            to: '/content-moderation/usage',
            labelKey: 'nav.contentModerationUsage',
            icon: 'wallet',
            tone: 'amber',
          },
        ],
      },
      { to: '/error-logs', labelKey: 'nav.errorLogs', icon: 'alert', tone: 'slate' },
    ],
  },
  {
    titleKey: 'nav.sections.system',
    items: [
      {
        to: '/admin-accounts',
        labelKey: 'nav.adminAccounts',
        icon: 'shieldUser',
        tone: 'indigo',
      },
      {
        to: '/announcements',
        labelKey: 'nav.announcements',
        icon: 'megaphone',
        tone: 'amber',
        sample: true,
      },
      {
        to: '/settings',
        labelKey: 'nav.settings',
        icon: 'settings',
        tone: 'slate',
        sample: true,
      },
    ],
  },
];

const flatten = (items: readonly AdminNavItem[]): AdminNavItem[] =>
  items.flatMap(item => [
    ...(item.defaultChild ? [] : [item]),
    ...flatten(item.children ?? []),
  ]);

const ALL_ITEMS: readonly AdminNavItem[] = flatten(
  NAV_SECTIONS.flatMap(section => section.items),
);

const matches = (item: AdminNavItem, pathname: string): boolean =>
  item.to === '/'
    ? pathname === '/'
    : // Anchored on a segment boundary so `/settings-export` cannot resolve to
      // the `/settings` entry.
      pathname === item.to || pathname.startsWith(`${item.to}/`);

/**
 * Longest matching nav entry for a pathname, used for the breadcrumb trail and
 * for the page's own icon. `/users/6f3a…` resolves to the Users entry so a
 * detail route still names its parent module.
 */
export const findNavItem = (pathname: string): AdminNavItem | undefined =>
  ALL_ITEMS.filter(item => matches(item, pathname)).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];

/** The top-level entry a pathname belongs to, for the first breadcrumb hop. */
export const findNavParent = (pathname: string): AdminNavItem | undefined =>
  NAV_SECTIONS.flatMap(section => section.items).find(
    item => matches(item, pathname) || item.children?.some(child => matches(child, pathname)),
  );
